/**
 * FiduciaryIdentitySection — Identity block for the Fiduciary person dialog.
 *
 * Contains the Individual-shared fields (Fiduciary Name + DOB + SSN) PLUS
 * fiduciary-specific extras:
 *   - Sub-type picker (RepPayee / Conservator / VAFiduciary) — drives customerType
 *   - Beneficiary / Ward Name + optional CIF link
 *   - Appointment Authority dropdown (Court / SSA / VA / Other)
 *   - Appointment Date
 *   - Appointment reference # (dynamic label — Court Case # / SSA Claim # / VA File #)
 *
 * Mirrors `IndividualIdentitySection.tsx` styling + conventions. CitizenSHIP
 * section is OMITTED per build-card rules (fiduciary's citizenship is irrelevant
 * to the relationship; they're acting as a legal appointee, not opening as a
 * personal customer).
 *
 * All fiduciary-specific fields live on `person.extra` for v0.1.37; wave-3
 * promotes them to the main RaoPerson shape.
 *
 * TODO(bill): fiduciary walkthrough needed — §15 deferred items surfaced as
 * comments inline with the affected controls.
 */

import { useEffect, type CSSProperties } from 'react';
import { DateTextInput, Pill } from '@/components/primitives';
import { formatSsn } from '@/utils/ssn';
import type { RaoPerson, RaoTinCode } from '../../../types';
import { ageFromDob } from '../../../utils/age';
import type { DerivationResult } from '../individual/useIndividualDerivation';
import { NAME_SUFFIXES } from '../individual/individual-config';
import {
  FIDUCIARY_SUB_TYPES,
  FIDUCIARY_APPOINTMENT_AUTHORITIES,
  APPOINTMENT_REF_FIELDS,
  FIDUCIARY_HELPER_TEXT,
  FIDUCIARY_SUB_TYPE_BY_VALUE,
} from './fiduciary-config';

// -----------------------------------------------------------------------------
// Shared leaf styles (mirror IndividualIdentitySection)
// -----------------------------------------------------------------------------

const labelStyle: CSSProperties = {
  fontSize: 12,
  color: 'var(--ink-600)',
  display: 'block',
  marginBottom: 4,
};

const inputStyle: CSSProperties = {
  width: '100%',
  padding: '8px 10px',
  borderRadius: 4,
  border: '1px solid var(--ink-200)',
  fontSize: 14,
  boxSizing: 'border-box',
};

const readOnlyStyle: CSSProperties = {
  ...inputStyle,
  background: 'var(--field-readonly-bg, #f5f5f5)',
  color: 'var(--ink-700)',
};

const helperTextStyle: CSSProperties = {
  fontSize: 11,
  color: 'var(--ink-600)',
  fontStyle: 'italic',
  marginTop: 4,
};

function changedFieldStyle(base: CSSProperties, changed: boolean): CSSProperties {
  return changed
    ? { ...base, borderLeft: '3px solid var(--pcb-blue, #2968ad)', background: 'var(--field-changed-bg, #f0f7ff)', color: 'var(--ink-900, #1d1d1f)' }
    : base;
}

// -----------------------------------------------------------------------------
// Extra-field helpers
// -----------------------------------------------------------------------------

function readExtra(person: RaoPerson, key: string): string {
  const v = person.extra?.[key];
  return typeof v === 'string' ? v : '';
}

function patchExtra(person: RaoPerson, patch: Record<string, unknown>): Partial<RaoPerson> {
  return { extra: { ...(person.extra ?? {}), ...patch } };
}

// -----------------------------------------------------------------------------
// Props
// -----------------------------------------------------------------------------

export interface FiduciaryIdentitySectionProps {
  person: RaoPerson;
  onChange: (patch: Partial<RaoPerson>) => void;
  mode: 'new' | 'existing';
  isChanged?: (field: string) => boolean;
  derivation: DerivationResult;
  readOnly?: boolean;
}

// -----------------------------------------------------------------------------
// Component
// -----------------------------------------------------------------------------

export function FiduciaryIdentitySection({
  person,
  onChange,
  mode,
  isChanged,
  derivation,
  readOnly = false,
}: FiduciaryIdentitySectionProps): JSX.Element {
  const isExisting = mode === 'existing';
  const ic = isChanged ?? ((_: string) => false);

  // Read extras
  const wardFirstName   = readExtra(person, 'wardFirstName');
  const wardLastName    = readExtra(person, 'wardLastName');
  const wardCifId       = readExtra(person, 'wardCifId');
  const appointmentAuthority = readExtra(person, 'appointmentAuthority');
  const appointmentDate = readExtra(person, 'appointmentDate');
  const appointmentRef  = readExtra(person, 'appointmentRef');

  // Current sub-type (drives UI shape).
  const currentSubType = person.customerType ?? 'RepPayee';
  const subTypeSpec = FIDUCIARY_SUB_TYPE_BY_VALUE[currentSubType];
  const refFieldSpec = APPOINTMENT_REF_FIELDS[currentSubType];

  // Lock TinCode to 'S' (SSN) for Fiduciary group — all 3 sub-types use the
  // beneficiary's/fiduciary's SSN per card.
  useEffect(() => {
    if (person.tinCode !== 'S') {
      onChange({ tinCode: 'S' as RaoTinCode });
    }
  }, [person.tinCode, onChange]);

  const age = ageFromDob(person.dob);

  // Handler: when sub-type changes, also default appointmentAuthority to the
  // canonical authority for that sub-type (officer can still override, but
  // the override trips the supervisor gate per useFiduciaryDerivation).
  const handleSubTypeChange = (nextValue: string): void => {
    const spec = FIDUCIARY_SUB_TYPE_BY_VALUE[nextValue];
    const patch: Partial<RaoPerson> = {
      customerType: nextValue as RaoPerson['customerType'],
      ...(spec?.callClass ? { callClass: spec.callClass } : {}),
    };
    // Also default appointmentAuthority to canonical when officer hasn't
    // picked one yet (or it currently matches the previous sub-type's default).
    if (spec?.canonicalAuthority && !appointmentAuthority) {
      onChange({
        ...patch,
        ...patchExtra(person, { appointmentAuthority: spec.canonicalAuthority }),
      });
    } else {
      onChange(patch);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {/* Entity type chip + derivation note (mirror Individual layout) */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: '10px 12px',
          background: 'var(--info-bg)',
          border: '1px solid var(--info-line)',
          borderRadius: 6,
        }}
      >
        <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-700)' }}>
          Entity Type:
        </span>
        <Pill variant="info">{currentSubType}</Pill>
        <span style={{ fontSize: 11, color: 'var(--ink-600)' }}>(manual sub-type pick)</span>
        {derivation.promotedFrom && (
          <span style={{ fontSize: 11, color: 'var(--pcb-blue)', marginLeft: 'auto' }}>
            updated from &lsquo;{derivation.promotedFrom}&rsquo;
          </span>
        )}
      </div>

      {/* Supervisor-approval gate */}
      {derivation.requiresSupervisorApproval && (
        <div
          role="alert"
          style={{
            padding: '10px 14px',
            background: 'var(--warn-bg)',
            border: '1px solid var(--warn-line)',
            borderRadius: 6,
            fontSize: 13,
            color: 'var(--ink-900)',
          }}
        >
          <strong>Supervisor approval required.</strong> This fiduciary sub-type or the
          appointment-authority pairing triggers a manager signoff before the CIF can be
          saved. A supervisor must review the court order / appointment packet.
        </div>
      )}

      {/* Sub-type picker (RADIO) ------------------------------------------ */}
      <div>
        <label style={labelStyle}>Fiduciary sub-type</label>
        <div style={{ fontSize: 11, color: 'var(--ink-600)', marginBottom: 8 }}>
          {FIDUCIARY_HELPER_TEXT.subTypeIntro}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
          {FIDUCIARY_SUB_TYPES.map((t) => {
            const selected = currentSubType === t.value;
            return (
              <label
                key={t.value}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 4,
                  padding: '10px 12px',
                  border: selected
                    ? '2px solid var(--pcb-blue, #2968ad)'
                    : '1px solid var(--ink-200)',
                  borderRadius: 6,
                  background: selected ? 'var(--field-changed-bg, #f0f7ff)' : 'var(--surface-card, #fff)',
                  cursor: readOnly ? 'not-allowed' : 'pointer',
                  opacity: readOnly ? 0.7 : 1,
                }}
              >
                <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <input
                    type="radio"
                    name="rao-fid-subtype"
                    value={t.value}
                    checked={selected}
                    onChange={() => handleSubTypeChange(t.value)}
                    disabled={readOnly || isExisting}
                  />
                  <strong style={{ fontSize: 13 }}>{t.label}</strong>
                </span>
                {t.hint && (
                  <span style={{ fontSize: 11, color: 'var(--ink-600)' }}>{t.hint}</span>
                )}
              </label>
            );
          })}
        </div>
      </div>

      {/* Fiduciary Name row: first / middle / last / suffix -------------- */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr 1fr 100px',
          gap: 12,
        }}
      >
        <div>
          <label style={labelStyle} htmlFor="rao-fid-first">Fiduciary first name</label>
          <input
            id="rao-fid-first"
            type="text"
            value={person.firstName ?? ''}
            onChange={(e) => onChange({ firstName: e.currentTarget.value })}
            readOnly={readOnly}
            style={changedFieldStyle(readOnly ? readOnlyStyle : inputStyle, ic('firstName'))}
            title="Fiduciary first name"
          />
        </div>
        <div>
          <label style={labelStyle} htmlFor="rao-fid-middle">Middle initial</label>
          <input
            id="rao-fid-middle"
            type="text"
            value={person.middleName ?? ''}
            onChange={(e) => onChange({ middleName: e.currentTarget.value })}
            readOnly={readOnly}
            style={changedFieldStyle(readOnly ? readOnlyStyle : inputStyle, ic('middleName'))}
            maxLength={1}
            placeholder="Optional"
          />
        </div>
        <div>
          <label style={labelStyle} htmlFor="rao-fid-last">Fiduciary last name</label>
          <input
            id="rao-fid-last"
            type="text"
            value={person.lastName ?? ''}
            onChange={(e) => onChange({ lastName: e.currentTarget.value })}
            readOnly={readOnly}
            style={changedFieldStyle(readOnly ? readOnlyStyle : inputStyle, ic('lastName'))}
            title="Fiduciary last name"
          />
        </div>
        <div>
          <label style={labelStyle} htmlFor="rao-fid-suffix">Suffix</label>
          <input
            id="rao-fid-suffix"
            list="rao-fid-suffix-options"
            type="text"
            value={person.suffix ?? ''}
            onChange={(e) => onChange({ suffix: e.currentTarget.value })}
            readOnly={readOnly}
            style={changedFieldStyle(readOnly ? readOnlyStyle : inputStyle, ic('suffix'))}
            maxLength={10}
            placeholder="— / Jr / Sr …"
            autoComplete="off"
          />
          <datalist id="rao-fid-suffix-options">
            {NAME_SUFFIXES.filter((s) => s.value !== '').map((s) => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </datalist>
        </div>
      </div>

      {/* SSN / TIN / DOB ------------------------------------------------- */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 140px 1fr', gap: 12 }}>
        <div>
          <label style={labelStyle} htmlFor="rao-fid-ssn">
            Fiduciary SSN{' '}
            {person.taxId && person.taxId.length === 9 && (
              <span style={{ color: 'var(--ink-600)', fontWeight: 400 }}>
                ({formatSsn(person.taxId)})
              </span>
            )}
          </label>
          <input
            id="rao-fid-ssn"
            type="text"
            value={person.taxId ?? ''}
            onChange={(e) =>
              onChange({ taxId: e.currentTarget.value.replace(/\D/g, '').slice(0, 9) })
            }
            readOnly={isExisting || readOnly}
            style={changedFieldStyle(isExisting || readOnly ? readOnlyStyle : inputStyle, ic('taxId'))}
            maxLength={9}
            placeholder="123456789"
            title="Fiduciary's own SSN (NOT the beneficiary's)"
          />
          {/* TODO(bill): fiduciary walkthrough needed — card says TinCode for
              RepPayee is beneficiary's SSN on the CIF. Need to clarify whose
              SSN lives on THIS record vs. the linked beneficiary CIF. */}
        </div>
        <div>
          <label style={labelStyle} htmlFor="rao-fid-tin">TIN code</label>
          <select
            id="rao-fid-tin"
            value={person.tinCode ?? 'S'}
            disabled
            style={readOnlyStyle}
            title="TIN code (locked to SSN for Fiduciary group)"
          >
            <option value="S">S · SSN</option>
          </select>
        </div>
        <div>
          <label style={labelStyle} htmlFor="rao-fid-dob">Fiduciary DOB</label>
          {/* Typed MM/DD/YYYY (Collette Cruse 2026-10-16: number-keypad entry). */}
          <DateTextInput
            id="rao-fid-dob"
            title="Fiduciary date of birth"
            value={person.dob ?? ''}
            onChange={(iso) => onChange({ dob: iso })}
            /* Editable for existing CIFs too (Bill 2026-07-17) — a wrong DOB must be
               correctable from Add Existing / Edit CIF, not locked at first capture. */
            readOnly={readOnly}
            style={changedFieldStyle(readOnly ? readOnlyStyle : inputStyle, ic('dob'))}
          />
          {age !== null && (
            <div style={{ fontSize: 11, color: 'var(--ink-600)', marginTop: 2 }}>
              Age: <strong>{age}</strong>
            </div>
          )}
        </div>
      </div>

      {/* Beneficiary / Ward block --------------------------------------- */}
      <div
        style={{
          padding: '12px 14px',
          background: 'var(--surface-subtle, #fafbfc)',
          border: '1px solid var(--ink-200)',
          borderRadius: 6,
        }}
      >
        <div
          style={{
            fontSize: 12,
            fontWeight: 600,
            color: 'var(--ink-700)',
            marginBottom: 8,
          }}
        >
          Beneficiary / Ward / Veteran (Protected Person)
        </div>
        <div style={{ fontSize: 11, color: 'var(--ink-600)', marginBottom: 10 }}>
          {FIDUCIARY_HELPER_TEXT.wardIntro}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
          <div>
            <label style={labelStyle} htmlFor="rao-fid-ward-first">
              Protected-person first name
            </label>
            <input
              id="rao-fid-ward-first"
              type="text"
              value={wardFirstName}
              onChange={(e) =>
                onChange(patchExtra(person, { wardFirstName: e.currentTarget.value }))
              }
              readOnly={readOnly}
              style={changedFieldStyle(readOnly ? readOnlyStyle : inputStyle, ic('wardFirstName'))}
            />
          </div>
          <div>
            <label style={labelStyle} htmlFor="rao-fid-ward-last">
              Protected-person last name
            </label>
            <input
              id="rao-fid-ward-last"
              type="text"
              value={wardLastName}
              onChange={(e) =>
                onChange(patchExtra(person, { wardLastName: e.currentTarget.value }))
              }
              readOnly={readOnly}
              style={changedFieldStyle(readOnly ? readOnlyStyle : inputStyle, ic('wardLastName'))}
            />
          </div>
          <div>
            <label style={labelStyle} htmlFor="rao-fid-ward-cif">
              Protected-person CIF (optional)
            </label>
            <input
              id="rao-fid-ward-cif"
              type="text"
              value={wardCifId}
              onChange={(e) =>
                onChange(patchExtra(person, { wardCifId: e.currentTarget.value }))
              }
              readOnly={readOnly}
              style={changedFieldStyle(readOnly ? readOnlyStyle : inputStyle, ic('wardCifId'))}
              placeholder="e.g. 123456"
              maxLength={10}
            />
            {/* TODO(bill): fiduciary walkthrough needed — should this trigger
                a CustInq lookup to auto-fill the ward name fields above?
                Also: which RaoPerson field is the canonical link target
                (protectedPersonCifId vs conservateeCifId vs wardCifId)? */}
          </div>
        </div>
      </div>

      {/* Appointment block --------------------------------------------- */}
      <div
        style={{
          padding: '12px 14px',
          background: 'var(--surface-subtle, #fafbfc)',
          border: '1px solid var(--ink-200)',
          borderRadius: 6,
        }}
      >
        <div
          style={{
            fontSize: 12,
            fontWeight: 600,
            color: 'var(--ink-700)',
            marginBottom: 8,
          }}
        >
          Appointment Instrument
        </div>
        <div style={{ fontSize: 11, color: 'var(--ink-600)', marginBottom: 10 }}>
          {FIDUCIARY_HELPER_TEXT.appointmentIntro}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
          <div>
            <label style={labelStyle} htmlFor="rao-fid-appt-auth">Appointment authority</label>
            <select
              id="rao-fid-appt-auth"
              value={appointmentAuthority}
              onChange={(e) =>
                onChange(patchExtra(person, { appointmentAuthority: e.currentTarget.value }))
              }
              disabled={readOnly}
              style={changedFieldStyle(
                readOnly ? readOnlyStyle : inputStyle,
                ic('appointmentAuthority'),
              )}
            >
              <option value="">Select authority…</option>
              {FIDUCIARY_APPOINTMENT_AUTHORITIES.map((a) => (
                <option key={a.value} value={a.value}>
                  {a.label}
                </option>
              ))}
            </select>
            {subTypeSpec?.canonicalAuthority && (
              <div style={helperTextStyle}>
                Canonical for {currentSubType}: <strong>{subTypeSpec.canonicalAuthority}</strong>
                {appointmentAuthority &&
                  appointmentAuthority !== subTypeSpec.canonicalAuthority && (
                    <span style={{ color: 'var(--pcb-red, #b10505)' }}>
                      {' '}
                      — mismatch trips supervisor gate.
                    </span>
                  )}
              </div>
            )}
          </div>

          <div>
            <label style={labelStyle} htmlFor="rao-fid-appt-date">Appointment date (Field 257)</label>
            <input
              id="rao-fid-appt-date"
              type="date"
              value={appointmentDate}
              onChange={(e) =>
                onChange(patchExtra(person, { appointmentDate: e.currentTarget.value }))
              }
              readOnly={readOnly}
              style={changedFieldStyle(
                readOnly ? readOnlyStyle : inputStyle,
                ic('appointmentDate'),
              )}
            />
          </div>

          <div>
            <label style={labelStyle} htmlFor="rao-fid-appt-ref">
              {refFieldSpec?.label ?? 'Appointment reference #'} (Field 255)
            </label>
            <input
              id="rao-fid-appt-ref"
              type="text"
              value={appointmentRef}
              onChange={(e) =>
                onChange(patchExtra(person, { appointmentRef: e.currentTarget.value }))
              }
              readOnly={readOnly}
              style={changedFieldStyle(
                readOnly ? readOnlyStyle : inputStyle,
                ic('appointmentRef'),
              )}
              placeholder={refFieldSpec?.placeholder ?? ''}
              maxLength={30}
            />
            {refFieldSpec?.hint && <div style={helperTextStyle}>{refFieldSpec.hint}</div>}
          </div>
        </div>
      </div>

      {/* TODO(bill): fiduciary walkthrough needed — additional sections per
          card that are NOT scaffolded here (deferred for §15 walkthrough):
            - Conservator: Public Administrator override (Dana R Sooter lookup)
            - Conservator: Case.Net court-records link (MO)
            - RepPayee: Stops/Holds auto-message (expiration 12/31/2099)
            - VAFiduciary: Signing-authority manipulation (Veteran = Not Authorized)
            - USDAGrant: Hardcoded USDA agent auto-add-on-save
            - MedicalSetAside: Packet-of-guidelines upload
          These are out of scope for v0.1.37 scaffolding. */}
    </div>
  );
}
